import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck, Building2, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { LoadingState, EmptyState } from "@/components/dashboard/common/PageShell";

const AGENCY_TYPES = ["creator_management", "venue_oversight", "platform_control"] as const;
type AgencyType = (typeof AGENCY_TYPES)[number];

const LABEL: Record<AgencyType, string> = {
  creator_management: "Creator Management",
  venue_oversight: "Venue Oversight",
  platform_control: "Platform Control",
};

interface AgencyProfile {
  id: string;
  username: string | null;
  full_name: string | null;
  user_type: string | null;
  portal_id: string | null;
  created_at: string;
}

const useAgencySignups = () =>
  useQuery({
    queryKey: ["agency-signups"],
    queryFn: async () => {
      const client = supabase as unknown as { from: (t: string) => any };
      const [{ data: profiles, error }, { data: roles }] = await Promise.all([
        client
          .from("profiles")
          .select("id, username, full_name, user_type, portal_id, created_at")
          .in("user_type", AGENCY_TYPES as unknown as string[])
          .order("created_at", { ascending: false }),
        client.from("user_roles").select("user_id, role"),
      ]);
      if (error) throw error;
      const granted = new Set((roles ?? []).map((r: { user_id: string }) => r.user_id));
      return {
        pending: ((profiles ?? []) as AgencyProfile[]).filter((p) => !granted.has(p.id)),
        approved: ((profiles ?? []) as AgencyProfile[]).filter((p) => granted.has(p.id)),
      };
    },
  });

const useGrantAgencyRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const client = supabase as unknown as { from: (t: string) => any };
      const { data: me } = await supabase.auth.getUser();
      const { error } = await client
        .from("user_roles")
        .insert({ user_id: userId, role, granted_by: me.user?.id ?? null });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agency-signups"] });
      qc.invalidateQueries({ queryKey: ["agency-members"] });
    },
  });
};

const useRevokeAgencyRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const client = supabase as unknown as { from: (t: string) => any };
      const { error } = await client.from("user_roles").delete().eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agency-signups"] }),
  });
};

const Row = ({
  profile,
  action,
}: {
  profile: AgencyProfile;
  action: React.ReactNode;
}) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-800 bg-gray-950/60 p-4">
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
      <Building2 className="h-5 w-5 text-white" />
    </div>
    <div className="min-w-[220px] flex-1">
      <p className="font-semibold text-white">{profile.full_name || profile.username || "Unnamed agency"}</p>
      <p className="text-xs text-gray-500">
        {LABEL[(profile.user_type ?? "creator_management") as AgencyType]} · Portal {profile.portal_id ?? "pending"} ·{" "}
        {format(new Date(profile.created_at), "d MMM yyyy")}
      </p>
    </div>
    {action}
  </div>
);

const AgencyApprovals = () => {
  const { data, isLoading } = useAgencySignups();
  const grant = useGrantAgencyRole();
  const revoke = useRevokeAgencyRole();

  const pending = data?.pending ?? [];
  const approved = data?.approved ?? [];
  const stats = useMemo(
    () => [
      { label: "Awaiting approval", value: pending.length.toString() },
      { label: "Active agency portals", value: approved.length.toString() },
    ],
    [pending.length, approved.length],
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Agency onboarding</h2>
        <p className="text-sm text-gray-400">
          Approve agency sign-ups to activate their portal ID and unlock roster, bookings and analytics modules.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {stats.map((s) => (
          <Card key={s.label} className="bg-gray-900/60 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">{s.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-white">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <LoadingState label="Loading agency sign-ups…" />
      ) : (
        <>
          <Card className="bg-gray-900/60 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-amber-400" /> Pending approval
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pending.length === 0 ? (
                <EmptyState
                  icon={ShieldCheck}
                  title="No pending agencies"
                  description="New agency sign-ups from the agency portal appear here for approval."
                />
              ) : (
                pending.map((p) => (
                  <Row
                    key={p.id}
                    profile={p}
                    action={
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          disabled={grant.isPending}
                          className="gap-1"
                          onClick={() =>
                            grant.mutate(
                              { userId: p.id, role: p.user_type as string },
                              {
                                onSuccess: () => toast.success("Agency approved — portal access granted"),
                                onError: (e) => toast.error(e instanceof Error ? e.message : "Approval failed"),
                              },
                            )
                          }
                        >
                          {grant.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <BadgeCheck className="h-3 w-3" />}{" "}
                          Approve
                        </Button>
                      </div>
                    }
                  />
                ))
              )}
            </CardContent>
          </Card>

          <Card className="bg-gray-900/60 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-base">Active agency portals</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {approved.length === 0 ? (
                <EmptyState icon={Building2} title="No active agencies" description="Approved agencies show up here." />
              ) : (
                approved.map((p) => (
                  <Row
                    key={p.id}
                    profile={p}
                    action={
                      <div className="flex items-center gap-2">
                        <Badge className="border-0 bg-emerald-600">Active</Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 border-gray-700 text-red-400"
                          onClick={() =>
                            revoke.mutate(p.id, {
                              onSuccess: () => toast.success("Portal access revoked"),
                              onError: (e) => toast.error(e instanceof Error ? e.message : "Could not revoke"),
                            })
                          }
                        >
                          <XCircle className="h-3 w-3" /> Revoke
                        </Button>
                      </div>
                    }
                  />
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AgencyApprovals;
